import Navbar from "../../components/navbar/Navbar";
import "./home.scss";
import { useEffect, useState } from "react";
import { auth, db } from "../../firebase";
import { onAuthStateChanged } from "firebase/auth";
import {
  collection,
  query,
  where,
  getCountFromServer,
  getDocs,
  orderBy,
  limit,
} from "firebase/firestore";
import {
  Box,
  Container,
  Grid,
  Card,
  CardContent,
  Typography,
  Avatar,
  Stack,
  Button,
  Paper,
  useTheme,
  alpha,
  Skeleton,
} from "@mui/material";
import {
  LibraryBooks,
  PendingActions,
  Add,
  CurrencyRupee,
  ShoppingCart,
  Visibility,
  BookmarkBorder,
  AccountBalanceWallet,
} from "@mui/icons-material";
import { useNavigate } from "react-router-dom";

const Home = () => {
  const [totalBooks, setTotalBooks] = useState(0);
  const [pendingBooks, setPendingBooks] = useState(0);
  const [totalRevenue, setTotalRevenue] = useState(0);
  const [totalOrders, setTotalOrders] = useState(0);
  const [recentBooks, setRecentBooks] = useState([]);

  const [loadingBooks, setLoadingBooks] = useState(true);
  const [loadingPending, setLoadingPending] = useState(true);
  const [loadingRevenue, setLoadingRevenue] = useState(true);
  const [loadingOrders, setLoadingOrders] = useState(true);
  const [loadingRecentBooks, setLoadingRecentBooks] = useState(true);
  const [authLoading, setAuthLoading] = useState(true);

  const theme = useTheme();
  const navigate = useNavigate();

  useEffect(() => {
    document.title = "Publisher Dashboard - Wishallbook";

    // Listen for auth state changes
    const unsubscribe = onAuthStateChanged(auth, (user) => {
      setAuthLoading(false);
      if (user) {
        fetchDashboardData(user.uid);
      } else {
        // User is not authenticated, stop all loading states
        setLoadingBooks(false);
        setLoadingPending(false);
        setLoadingRevenue(false);
        setLoadingOrders(false);
        setLoadingRecentBooks(false);
      }
    });

    // Cleanup subscription on unmount
    return () => unsubscribe();
  }, []);

  const fetchDashboardData = async (publisherID) => {
    try {
      if (!publisherID) {
        setLoadingBooks(false);
        setLoadingPending(false);
        setLoadingRevenue(false);
        setLoadingOrders(false);
        setLoadingRecentBooks(false);
        return;
      }

      // Fetch all data independently
      fetchTotalBooks(publisherID);
      fetchPendingBooks(publisherID);
      fetchRevenueAndOrders(publisherID);
      fetchRecentBooks(publisherID);
    } catch (error) {
      console.error("Error fetching dashboard data:", error);
    }
  };

  const fetchTotalBooks = async (publisherID) => {
    try {
      const booksQuery = query(
        collection(db, "eLibrary"),
        where("publisherID", "==", publisherID)
      );
      const booksSnapshot = await getCountFromServer(booksQuery);
      const count = booksSnapshot.data().count;
      setTotalBooks(count);
    } catch (error) {
      console.error("Error fetching total books:", error);
      setTotalBooks(0);
    } finally {
      setLoadingBooks(false);
    }
  };

  const fetchPendingBooks = async (publisherID) => {
    try {
      const pendingQuery = query(
        collection(db, "pendingBooks"),
        where("publisherID", "==", publisherID)
      );
      const pendingSnapshot = await getCountFromServer(pendingQuery);
      const count = pendingSnapshot.data().count;
      setPendingBooks(count);
    } catch (error) {
      console.error("Error fetching pending books:", error);
      setPendingBooks(0);
    } finally {
      setLoadingPending(false);
    }
  };

  const fetchRevenueAndOrders = async (publisherID) => {
    try {
      // Get publisher's books first
      const publisherBooksQuery = query(
        collection(db, "eLibrary"),
        where("publisherID", "==", publisherID)
      );
      const publisherBooksSnapshot = await getDocs(publisherBooksQuery);
      const publisherBookIds = publisherBooksSnapshot.docs.map((doc) => doc.id);

      // Fetch orders to calculate revenue and order count
      const ordersQuery = query(collection(db, "orders"));
      const ordersSnapshot = await getDocs(ordersQuery);

      let revenue = 0;
      let orders = 0;

      ordersSnapshot.docs.forEach((doc) => {
        const orderData = doc.data();
        if (publisherBookIds.includes(orderData.bookId)) {
          orders++;
          revenue += orderData.amount || 0;
        }
      });

      setTotalRevenue(revenue);
      setTotalOrders(orders);
    } catch (error) {
      console.error("Error fetching revenue and orders:", error);
      setTotalRevenue(0);
      setTotalOrders(0);
    } finally {
      setLoadingRevenue(false);
      setLoadingOrders(false);
    }
  };

  const fetchRecentBooks = async (publisherID) => {
    try {
      const booksQuery = query(
        collection(db, "eLibrary"),
        where("publisherID", "==", publisherID),
        orderBy("publish_on", "desc"),
        limit(6)
      );
      const booksSnapshot = await getDocs(booksQuery);

      const books = booksSnapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      }));

      setRecentBooks(books);
    } catch (error) {
      console.error("Error fetching recent books:", error);
    } finally {
      setLoadingRecentBooks(false);
    }
  };

  const quickActions = [
    {
      title: "Publish New Book",
      description: "Start your publishing journey",
      icon: Add,
      action: () => navigate("/add-book"),
    },
    {
      title: "My Library",
      description: "Manage your collection",
      icon: LibraryBooks,
      action: () => navigate("/library"),
    },
    {
      title: "Earnings",
      description: "Track your revenue",
      icon: AccountBalanceWallet,
      action: () => navigate("/account"),
    },
  ];

  return (
    <Box sx={{ minHeight: "100vh", bgcolor: "#ffffff" }}>
      <Navbar />

      <Container maxWidth="xl" sx={{ py: 4 }}>
        {/* Header Section */}
        <Box sx={{ mb: 6 }}>
          <Typography variant="h4" fontWeight={600} color="text.primary" mb={1}>
            Dashboard
          </Typography>
          <Typography variant="body1" color="text.secondary">
            Manage your books and track your publishing progress
          </Typography>
        </Box>

        {/* Stats Cards */}
        <Grid container spacing={3} sx={{ mb: 6 }}>
          <Grid item xs={12} sm={6} md={3}>
            <Card sx={{ p: 3, border: "1px solid #e0e0e0", boxShadow: "none" }}>
              <Stack direction="row" alignItems="center" spacing={2}>
                <Avatar sx={{ bgcolor: "#f5f5f5", color: "text.primary" }}>
                  <LibraryBooks />
                </Avatar>
                <Box>
                  <Typography variant="h5" fontWeight={600}>
                    {loadingBooks ? "..." : totalBooks}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Published Books
                  </Typography>
                </Box>
              </Stack>
            </Card>
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <Card sx={{ p: 3, border: "1px solid #e0e0e0", boxShadow: "none" }}>
              <Stack direction="row" alignItems="center" spacing={2}>
                <Avatar sx={{ bgcolor: "#f5f5f5", color: "text.primary" }}>
                  <CurrencyRupee />
                </Avatar>
                <Box>
                  <Typography variant="h5" fontWeight={600}>
                    {loadingRevenue
                      ? "..."
                      : `₹${totalRevenue.toLocaleString()}`}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Total Revenue
                  </Typography>
                </Box>
              </Stack>
            </Card>
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <Card sx={{ p: 3, border: "1px solid #e0e0e0", boxShadow: "none" }}>
              <Stack direction="row" alignItems="center" spacing={2}>
                <Avatar sx={{ bgcolor: "#f5f5f5", color: "text.primary" }}>
                  <ShoppingCart />
                </Avatar>
                <Box>
                  <Typography variant="h5" fontWeight={600}>
                    {loadingOrders ? "..." : totalOrders}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Total Orders
                  </Typography>
                </Box>
              </Stack>
            </Card>
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <Card sx={{ p: 3, border: "1px solid #e0e0e0", boxShadow: "none" }}>
              <Stack direction="row" alignItems="center" spacing={2}>
                <Avatar sx={{ bgcolor: "#f5f5f5", color: "text.primary" }}>
                  <PendingActions />
                </Avatar>
                <Box>
                  <Typography variant="h5" fontWeight={600}>
                    {loadingPending ? "..." : pendingBooks}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Pending Review
                  </Typography>
                </Box>
              </Stack>
            </Card>
          </Grid>
        </Grid>

        {/* Quick Actions */}
        <Box sx={{ mb: 6 }}>
          <Typography variant="h5" fontWeight={600} mb={3}>
            Quick Actions
          </Typography>
          <Grid container spacing={3}>
            {quickActions.map((action, index) => (
              <Grid item xs={12} md={4} key={index}>
                <Card
                  sx={{
                    p: 3,
                    border: "1px solid #e0e0e0",
                    boxShadow: "none",
                    cursor: "pointer",
                    transition: "all 0.2s ease",
                    "&:hover": {
                      boxShadow: "0 4px 12px rgba(0,0,0,0.1)",
                      transform: "translateY(-2px)",
                    },
                  }}
                  onClick={action.action}
                >
                  <Stack direction="row" alignItems="center" spacing={2}>
                    <Avatar sx={{ bgcolor: "#f5f5f5", color: "text.primary" }}>
                      <action.icon />
                    </Avatar>
                    <Box>
                      <Typography variant="h6" fontWeight={600}>
                        {action.title}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {action.description}
                      </Typography>
                    </Box>
                  </Stack>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Box>

        {/* Recent Books */}
        <Box sx={{ mb: 6 }}>
          <Stack
            direction="row"
            justifyContent="space-between"
            alignItems="center"
            mb={3}
          >
            <Typography variant="h5" fontWeight={600}>
              Recent Books
            </Typography>
            <Button
              variant="outlined"
              startIcon={<Visibility />}
              onClick={() => navigate("/library")}
              sx={{
                color: "text.primary",
                borderColor: "#e0e0e0",
                "&:hover": {
                  borderColor: "text.primary",
                  bgcolor: "#f5f5f5",
                },
              }}
            >
              View All
            </Button>
          </Stack>

          {loadingRecentBooks ? (
            <Grid container spacing={3}>
              {[1, 2, 3, 4, 5, 6].map((item) => (
                <Grid item xs={12} sm={6} md={4} lg={2} key={item}>
                  <Card sx={{ border: "1px solid #e0e0e0", boxShadow: "none" }}>
                    <Skeleton variant="rectangular" height={200} />
                    <CardContent>
                      <Skeleton variant="text" />
                      <Skeleton variant="text" width="60%" />
                    </CardContent>
                  </Card>
                </Grid>
              ))}
            </Grid>
          ) : recentBooks.length > 0 ? (
            <Grid container spacing={3}>
              {recentBooks.map((book) => (
                <Grid item xs={12} sm={6} md={4} lg={2} key={book.id}>
                  <Card
                    sx={{
                      border: "1px solid #e0e0e0",
                      boxShadow: "none",
                      transition: "all 0.2s ease",
                      "&:hover": {
                        boxShadow: "0 4px 12px rgba(0,0,0,0.1)",
                        transform: "translateY(-2px)",
                      },
                    }}
                  >
                    <Box
                      sx={{
                        height: 200,
                        backgroundImage: `url(${book.thumbImage})`,
                        backgroundSize: "cover",
                        backgroundPosition: "center",
                        position: "relative",
                      }}
                    >
                      <Box
                        sx={{
                          position: "absolute",
                          top: 8,
                          right: 8,
                          bgcolor: "rgba(0,0,0,0.7)",
                          color: "white",
                          px: 1,
                          py: 0.5,
                          borderRadius: 1,
                          fontSize: "0.75rem",
                        }}
                      >
                        {book.payment_type || "Free"}
                      </Box>
                    </Box>
                    <CardContent sx={{ p: 2 }}>
                      <Typography
                        variant="subtitle2"
                        fontWeight={600}
                        noWrap
                        mb={1}
                      >
                        {book.name}
                      </Typography>
                      <Stack direction="row" spacing={2} alignItems="center">
                        <Stack
                          direction="row"
                          alignItems="center"
                          spacing={0.5}
                        >
                          <Visibility
                            sx={{ fontSize: 16, color: "text.secondary" }}
                          />
                          <Typography variant="caption" color="text.secondary">
                            {book.views || 0}
                          </Typography>
                        </Stack>
                        <Stack
                          direction="row"
                          alignItems="center"
                          spacing={0.5}
                        >
                          <ShoppingCart
                            sx={{ fontSize: 16, color: "text.secondary" }}
                          />
                          <Typography variant="caption" color="text.secondary">
                            {book.unitSold || 0}
                          </Typography>
                        </Stack>
                      </Stack>
                      {book.payment_type === "Paid" && (
                        <Typography variant="subtitle1" fontWeight={600} mt={1}>
                          ₹
                          {Math.ceil(
                            book.price - (book.price / 100) * book.discount
                          )}
                        </Typography>
                      )}
                    </CardContent>
                  </Card>
                </Grid>
              ))}
            </Grid>
          ) : (
            <Paper
              sx={{
                p: 6,
                textAlign: "center",
                border: "1px solid #e0e0e0",
                boxShadow: "none",
                bgcolor: "#fafafa",
              }}
            >
              <BookmarkBorder
                sx={{ fontSize: 64, color: "text.secondary", mb: 2 }}
              />
              <Typography variant="h6" color="text.secondary" mb={2}>
                No books published yet
              </Typography>
              <Button
                variant="contained"
                startIcon={<Add />}
                onClick={() => navigate("/add-book")}
                sx={{
                  bgcolor: "text.primary",
                  "&:hover": {
                    bgcolor: "text.secondary",
                  },
                }}
              >
                Publish Your First Book
              </Button>
            </Paper>
          )}
        </Box>

        {/* Performance Summary */}
        <Box>
          <Typography variant="h5" fontWeight={600} mb={3}>
            Performance Summary
          </Typography>
          <Grid container spacing={3}>
            <Grid item xs={12} md={4}>
              <Card
                sx={{
                  p: 3,
                  border: "1px solid #e0e0e0",
                  boxShadow: "none",
                  textAlign: "center",
                }}
              >
                <Typography variant="h4" fontWeight={600} mb={1}>
                  {loadingBooks || loadingRevenue
                    ? "..."
                    : `₹${
                        totalBooks > 0
                          ? Math.round(totalRevenue / totalBooks)
                          : 0
                      }`}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Average Revenue per Book
                </Typography>
              </Card>
            </Grid>
            <Grid item xs={12} md={4}>
              <Card
                sx={{
                  p: 3,
                  border: "1px solid #e0e0e0",
                  boxShadow: "none",
                  textAlign: "center",
                }}
              >
                <Typography variant="h4" fontWeight={600} mb={1}>
                  {loadingBooks || loadingOrders
                    ? "..."
                    : totalBooks > 0
                    ? Math.round(totalOrders / totalBooks)
                    : 0}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Average Orders per Book
                </Typography>
              </Card>
            </Grid>
            <Grid item xs={12} md={4}>
              <Card
                sx={{
                  p: 3,
                  border: "1px solid #e0e0e0",
                  boxShadow: "none",
                  textAlign: "center",
                }}
              >
                <Typography variant="h4" fontWeight={600} mb={1}>
                  {loadingPending ? "..." : pendingBooks}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Books Awaiting Approval
                </Typography>
              </Card>
            </Grid>
          </Grid>
        </Box>
      </Container>
    </Box>
  );
};

export default Home;
